import { FormProvider, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client/react";
import { isEqual, pick } from "lodash-es";

import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import { Context } from "types/generated";
import {
  PhaseName,
  adaptAllCommandsToFields,
  adaptFieldsToCommands,
  allPhaseHooks,
} from "utils/hooks";
import useTitle from "hooks/useTitle";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Tooltip from "ds/components/Tooltip";

import { UPDATE_CONTEXT } from "../gql";
import { ContextContext } from "../Context";
import ContextHooksItem from "./Item";
import { ContextHooksFormFields } from "./types";

const ContextHooks = () => {
  const { context, canUpdateContext } = useTypedContext(ContextContext);
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  useTitle(`Hooks · ${context.name}`);

  const [updateConfig, { loading: updateLoading }] = useMutation<{
    contextUpdateV2: Context;
  }>(UPDATE_CONTEXT, {
    refetchQueries: ["GetContext"],
  });
  const hooks = adaptFieldsToCommands(context.hooks);

  const form = useForm<ContextHooksFormFields>({
    defaultValues: {
      hooks: hooks,
    },
    mode: "onChange",
  });

  const hooksData = form.watch("hooks");
  const adaptedHooksFormData = adaptAllCommandsToFields(hooksData);
  const contextHooksData = pick(context.hooks, allPhaseHooks);

  const isDataChanged = !isEqual(contextHooksData, adaptedHooksFormData);

  useBlockNavigationModal({
    isNavigationBlocked: isDataChanged,
  });

  const onSave = () => {
    updateConfig({
      variables: {
        id: context.id,
        input: {
          name: context.name,
          description: context.description,
          labels: context.labels,
          hooks: adaptedHooksFormData,
        },
      },
    })
      .then(({ data }) => {
        if (data) {
          reportSuccess({
            message: "Hooks were updated",
          });
        }
      })
      .catch(onError);
  };

  return (
    <>
      <PageInfoHeader title="Hooks">
        {(canUpdateContext.result || !!canUpdateContext.message) && (
          <Tooltip
            active={!!canUpdateContext.message}
            on={(props) => (
              <Button
                {...props}
                variant="primary"
                onPress={onSave}
                loading={updateLoading}
                disabled={!canUpdateContext.result || updateLoading}
              >
                Save changes
              </Button>
            )}
          >
            {canUpdateContext.message}
          </Tooltip>
        )}
      </PageInfoHeader>
      <FormProvider {...form}>
        <Box direction="column" padding="large" gap="large">
          {Object.keys(hooks).map((item) => {
            return <ContextHooksItem key={item} item={item as PhaseName} />;
          })}
        </Box>
      </FormProvider>
    </>
  );
};

export default ContextHooks;
